import { Button } from "@/components/ui/baseUi/button"
import { StandardInsideInput } from "@/components/ui/forms/standardInsideInput"
import { cn } from "@/modules/lib/utils"
import {
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { HUChronoInput } from "@/modules/ui/components/huChronoInput"
import { type SimpleFormik } from "@/typings/formik"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { GetChronoDefaults, MAX_NUMBER_INPUT, MIN_NUMBER_INPUT } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useEffect, useState } from "react"

type ChronoSettingsTABATAFormProps = {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ChronoSettingsTABATAForm({ formik, className, ...props }: ChronoSettingsTABATAFormProps) {
  const [moreThanOneSet, setMoreThanOneSet] = useState(false)

  const getFieldProps = (name: string) => formik.getFieldProps(name as ExerciseInputKey)

  useEffect(() => {
    if (!formik.values.chronoTabata) {
      void formik.setFieldValue("chronoTabata", GetChronoDefaults(ChronoType.TABATA))
    }
  }, [])

  useEffect(() => {
    if (formik.values.chronoTabata?.numberOfSets && formik.values.chronoTabata.numberOfSets > 1) {
      setMoreThanOneSet(true)
    }
  }, [formik.values.chronoTabata])

  const addSet = () => {
    if (formik.values.chronoTabata) {
      setMoreThanOneSet(true)
      void formik.setFieldValue("chronoTabata.numberOfSets", formik.values.chronoTabata.numberOfSets + 1)
    }
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <div className="flex flex-col gap-1">
        <HUChronoInput
          name="chronoTabata.trainingTime"
          label={word("chrono.settings.label.training_time")}
          getFieldProps={getFieldProps}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage("chronoTabata.trainingTime", formik, { showAlways: true })}
        />

        <HUChronoInput
          name="chronoTabata.restTime"
          label={word("chrono.settings.label.rest_time")}
          getFieldProps={getFieldProps}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage("chronoTabata.restTime", formik, { showAlways: true })}
        />

        <StandardInsideInput
          label={word("chrono.settings.label.rounds")}
          name="chronoTabata.numberOfRounds"
          error={getFormErrorMessage("chronoTabata.numberOfRounds", formik)}
          getFieldProps={getFieldProps}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            type: "number",
            max: MAX_NUMBER_INPUT,
            min: MIN_NUMBER_INPUT,
            onChange: (e) => {
              const value = parseInt(e.target.value)
              if (value > MAX_NUMBER_INPUT) void formik.setFieldValue("chronoTabata.numberOfRounds", MAX_NUMBER_INPUT)
              else void formik.setFieldValue("chronoTabata.numberOfRounds", value)
            },
          }}
        />

        {moreThanOneSet && (
          <>
            <HUChronoInput
              name="chronoTabata.restBetweenSetsTime"
              label={word("chrono.settings.label.rest_between_sets")}
              getFieldProps={getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("chronoTabata.restBetweenSetsTime", formik, { showAlways: true })}
            />

            <StandardInsideInput
              label={word("chrono.settings.label.number_sets")}
              name="chronoTabata.numberOfSets"
              error={getFormErrorMessage("chronoTabata.numberOfSets", formik)}
              getFieldProps={getFieldProps}
              setFieldValue={formik.setFieldValue}
              inputProps={{
                type: "number",
                max: MAX_NUMBER_INPUT,
                min: MIN_NUMBER_INPUT,
                onChange: (e) => {
                  const sets = parseInt(e.target.value) ?? 0
                  if (sets === 1) {
                    void formik.setFieldValue("chronoTabata.restBetweenSetsTime", 0)
                    void formik.setFieldValue("chronoTabata.numberOfSets", sets)
                  } else if (sets > MAX_NUMBER_INPUT) {
                    void formik.setFieldValue("chronoTabata.numberOfSets", MAX_NUMBER_INPUT)
                  } else {
                    void formik.setFieldValue("chronoTabata.numberOfSets", sets)
                  }
                },
              }}
            />
          </>
        )}
      </div>

      {!moreThanOneSet && (
        <Button variant="outline" onClick={addSet}>
          <PlusIcon color="currentColor" className="text-textDefault-body" />
          {word("chrono.settings.label.add_set")}
        </Button>
      )}
    </div>
  )
}
